import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';

import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { addScheduledMessageButtonActions } from '@common/reducers/AddScheduledMessageButtonReducer';
import { MessageState } from '@common/types/interface';
import ScheduledMessageEditorPopup from '@extension/context/content/components/add-scheduled-message-button/scheduled-message-editor-popup';
import { CommonRootState } from '@common/types/common-root-state-type';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface DiagnosticsSchedulingSectionProps {
  userId: string;
}

type ScheduledMessageEntry = {
  id: string;
  timestamp: string;
};

export function DiagnosticsSchedulingSection({
  userId,
}: DiagnosticsSchedulingSectionProps) {
  const dispatch = useAppDispatch();
  const store = useStore<CommonRootState>();

  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessageEntry[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Track IDs before popup opens to detect new messages
  const idsBeforePopupRef = useRef<Set<string>>(new Set());

  // Track if popup is open (using 'open' which is what ScheduledMessageEditorPopup uses)
  const isPopupOpen = useAppSelector(state => state.AddScheduledMessageButtonReducer.open);
  const wasPopupOpenRef = useRef(false);

  // Get current store IDs
  const currentIds = useAppSelector(state => state.DBUserMessageReducer.ids as string[]) ?? [];

  // Detect when popup closes and capture new message IDs
  useEffect(() => {
    if (wasPopupOpenRef.current && !isPopupOpen) {
      // Popup just closed - check for new messages
      const newIds = currentIds.filter(id => !idsBeforePopupRef.current.has(id));

      if (newIds.length > 0) {
        const timestamp = new Date().toISOString();
        const newEntries: ScheduledMessageEntry[] = newIds.map(id => ({
          id,
          timestamp,
        }));

        setScheduledMessages(prev => [...prev, ...newEntries]);
      }
    }

    wasPopupOpenRef.current = isPopupOpen;
  }, [isPopupOpen, currentIds]);

  const handleOpenPopup = useCallback(() => {
    // Snapshot current IDs before opening popup
    idsBeforePopupRef.current = new Set(store.getState().DBUserMessageReducer.ids as string[] ?? []);

    dispatch(addScheduledMessageButtonActions.editMessage({
      message: 'BT Diagnostics E2E test',
      state: MessageState.pending,
      dueDate: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
      isRecurring: false,
      cancelIfReceived: false,
    } as any));
  }, [dispatch, store]);

  const handleCopyId = useCallback((id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  }, []);

  const handleCopyAllIds = useCallback(() => {
    const allIds = scheduledMessages.map(m => m.id).join(', ');
    navigator.clipboard.writeText(allIds);
    setCopiedId('all');
    setTimeout(() => setCopiedId(null), 1500);
  }, [scheduledMessages]);

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <h2 className="text-base font-semibold">Schedule Message (E2E Test)</h2>
      <p className="text-xs text-slate-500">
        Click "Open Scheduler Popup" to open the full scheduling dialog.
        Add recipients, compose your message, and click "Schedule Send".
        Scheduled message IDs will appear below for verification in the Message Store Lookup tool in the diagnostics list.
      </p>

      {/* Open Scheduler Popup Button */}
      <Button
        onClick={handleOpenPopup}
        data-testid="open-scheduler-popup"
      >
        Open Scheduler Popup
      </Button>

      {/* The popup component - renders when Redux state triggers it */}
      <ScheduledMessageEditorPopup />

      {/* Scheduled Messages List */}
      {scheduledMessages.length > 0 && (
        <div className="space-y-2" data-testid="scheduled-messages-list">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">
              Scheduled Messages ({scheduledMessages.length})
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyAllIds}
              className="text-xs h-7"
            >
              {copiedId === 'all' ? 'Copied!' : 'Copy All IDs'}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Copy message IDs below and paste into the Message Store Lookup tool in the diagnostics list to verify delivery status.
          </p>
          <div className="space-y-1">
            {scheduledMessages.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center gap-2 border rounded px-3 py-2 text-xs bg-white"
              >
                <Badge variant="default" className="text-[10px]">
                  scheduled
                </Badge>
                <code className="font-mono text-[11px] bg-slate-100 px-1 rounded flex-1">
                  {entry.id}
                </code>
                <button
                  type="button"
                  onClick={() => handleCopyId(entry.id)}
                  className="inline-flex items-center justify-center rounded hover:bg-slate-100 px-2 py-1 text-[10px]"
                  title="Copy message ID"
                >
                  {copiedId === entry.id ? (
                    <span className="text-green-600 font-medium">Copied!</span>
                  ) : (
                    <span className="text-slate-600">Copy</span>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
